import dayjs from 'dayjs';

/**
 * Estrutura e regras do módulo "Plano alimentar".
 *
 * Um plano tem refeições ordenadas; cada refeição tem itens; cada item pode ter
 * substituições. As chaves (`key`) são geradas no navegador e só servem para o
 * React e para a edição: o backend guarda o JSON como está.
 */

export const STATUS_ACTIVE = 'active';

export const UNIT_SUGGESTIONS = [
  'g',
  'ml',
  'unidade',
  'fatia',
  'colher de sopa',
  'colher de chá',
  'xícara',
  'copo',
  'porção',
  'concha',
  'pedaço',
];

export const MAX_MEALS = 20;
export const MAX_ITEMS_PER_MEAL = 40;
export const MAX_SUBSTITUTES_PER_ITEM = 10;

let keyCounter = 0;

export function newKey(prefix = 'k') {
  keyCounter += 1;
  return `${prefix}${Date.now().toString(36)}${keyCounter}`;
}

export function createSubstitute(values = {}) {
  return { key: newKey('s'), food: '', portion: '', unit: '', ...values };
}

export function createItem(values = {}) {
  return { key: newKey('i'), food: '', portion: '', unit: '', note: '', substitutes: [], ...values };
}

export function createMeal(values = {}) {
  return { key: newKey('m'), name: '', time: '', note: '', items: [], ...values };
}

/** Refeições com que um plano novo começa: nomes usuais, sem itens. */
export function defaultMeals() {
  return [
    createMeal({ name: 'Café da manhã', time: '07:30' }),
    createMeal({ name: 'Lanche da manhã', time: '10:00' }),
    createMeal({ name: 'Almoço', time: '12:30' }),
    createMeal({ name: 'Lanche da tarde', time: '16:00' }),
    createMeal({ name: 'Jantar', time: '19:30' }),
  ];
}

/** Garante chaves e listas em cada nível (dados vindos do servidor). */
export function normalizeMeals(meals) {
  const list = Array.isArray(meals) ? meals : [];
  return list.map((meal) => ({
    key: meal?.key || newKey('m'),
    name: meal?.name || '',
    time: meal?.time || '',
    note: meal?.note || '',
    items: (Array.isArray(meal?.items) ? meal.items : []).map((item) => ({
      key: item?.key || newKey('i'),
      food: item?.food || '',
      portion: item?.portion || '',
      unit: item?.unit || '',
      note: item?.note || '',
      substitutes: (Array.isArray(item?.substitutes) ? item.substitutes : []).map((sub) => ({
        key: sub?.key || newKey('s'),
        food: sub?.food || '',
        portion: sub?.portion || '',
        unit: sub?.unit || '',
      })),
    })),
  }));
}

/** Cópia das refeições com chaves novas: base de uma nova versão. */
export function cloneMeals(meals) {
  return normalizeMeals(meals).map((meal) => ({
    ...meal,
    key: newKey('m'),
    items: meal.items.map((item) => ({
      ...item,
      key: newKey('i'),
      substitutes: item.substitutes.map((sub) => ({ ...sub, key: newKey('s') })),
    })),
  }));
}

export function sortPlansByDateDesc(plans) {
  return [...(plans || [])].sort((a, b) => {
    const diff = dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    return diff !== 0 ? diff : Number(b.id) - Number(a.id);
  });
}

/** Plano vigente do paciente: o marcado como ativo ou, na falta, o mais recente. */
export function getCurrentPlan(plans) {
  const sorted = sortPlansByDateDesc(plans);
  return sorted.find((plan) => plan.status === STATUS_ACTIVE) || sorted[0] || null;
}

export function formatDate(date) {
  return date ? dayjs(date).format('DD/MM/YYYY') : '';
}

/** "3 colheres de sopa", "150 g" ou "" quando não há porção. */
export function formatPortion(item) {
  const portion = (item?.portion || '').trim();
  const unit = (item?.unit || '').trim();
  return [portion, unit].filter(Boolean).join(' ');
}

/** Texto curto de uma refeição: "Café da manhã, 07:30". */
export function mealSummary(meal) {
  return [meal?.name, meal?.time].filter(Boolean).join(', ');
}

export function countItems(meals) {
  return (meals || []).reduce((total, meal) => total + (meal.items ? meal.items.length : 0), 0);
}

/** Valida o plano antes de salvar; devolve a mensagem do primeiro problema ou null. */
export function validatePlan(meals) {
  if (!meals || meals.length === 0) return 'Adicione pelo menos uma refeição.';
  if (meals.length > MAX_MEALS) return `O plano aceita no máximo ${MAX_MEALS} refeições.`;
  for (let i = 0; i < meals.length; i += 1) {
    const meal = meals[i];
    if (!meal.name || !meal.name.trim()) return `Dê um nome à refeição ${i + 1}.`;
    if (meal.time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(meal.time)) return `Horário inválido em "${meal.name}". Use HH:MM.`;
    if (meal.items.length > MAX_ITEMS_PER_MEAL) return `"${meal.name}" tem itens demais (máximo ${MAX_ITEMS_PER_MEAL}).`;
    for (let j = 0; j < meal.items.length; j += 1) {
      if (meal.items[j].substitutes.length > MAX_SUBSTITUTES_PER_ITEM) {
        return `Um item de "${meal.name}" tem substituições demais (máximo ${MAX_SUBSTITUTES_PER_ITEM}).`;
      }
    }
  }
  return null;
}

/** Remove itens e substituições em branco antes de enviar. */
export function cleanMeals(meals) {
  return (meals || []).map((meal) => ({
    key: meal.key,
    name: (meal.name || '').trim(),
    time: (meal.time || '').trim(),
    note: (meal.note || '').trim(),
    items: (meal.items || [])
      .filter((item) => (item.food || '').trim())
      .map((item) => ({
        key: item.key,
        food: item.food.trim(),
        portion: (item.portion || '').trim(),
        unit: (item.unit || '').trim(),
        note: (item.note || '').trim(),
        substitutes: (item.substitutes || [])
          .filter((sub) => (sub.food || '').trim())
          .map((sub) => ({
            key: sub.key,
            food: sub.food.trim(),
            portion: (sub.portion || '').trim(),
            unit: (sub.unit || '').trim(),
          })),
      })),
  }));
}
